import { Country, PaymentTypes } from '../../types'

export interface PaymentMethodConfig {
  regex: RegExp
  labels: string[]
  type: string
  availableIn: Country[]
  priority?: number
}

export interface PaymentTypeConfig {
  label: string
  value: PaymentTypes
  checked?: boolean
}

export interface CountryConfiguration {
  code: Country
  name: string
  capitalizedName: string
  paymentMethods: PaymentMethodConfig[]
  paymentTypes: PaymentTypeConfig[]
}

const SHARED_PAYMENT_METHODS: PaymentMethodConfig[] = [
  // Chile
  {
    regex: /^Webpay$/i,
    labels: ['Tarjeta de débito', 'Tarjeta de crédito'],
    type: 'webpay',
    availableIn: [Country.CHILE],
    priority: 1,
  },
  {
    regex: /^Mach$/i,
    labels: ['Tarjeta MACH'],
    type: 'mach',
    availableIn: [Country.CHILE],
    priority: 2,
  },
  {
    regex: /^Flowpay$/i,
    labels: ['Flowpay'],
    type: 'flowpay',
    availableIn: [Country.CHILE],
    priority: 3,
  },
  {
    regex: /^Mercado Pago Checkout Brick$/i,
    labels: ['Tarjeta Crédito/Débito'],
    type: 'mercadopago',
    availableIn: [Country.CHILE],
    priority: 4,
  },
  {
    regex: /^Mercado Pago Checkout Pro$/i,
    labels: ['Mercado Pago Wallet'],
    type: 'mercadopago-pro',
    availableIn: [Country.CHILE],
    priority: 5,
  },
  // Bolivia (BGA, API-Market and QR are excluded from options, only used for QRID)
  {
    regex: /^Yape$/i,
    labels: ['Yape'],
    type: 'yape',
    availableIn: [Country.BOLIVIA],
    priority: 1,
  },
  {
    regex: /^(TDC|Cybersource)$/i,
    labels: ['Tarjeta de débito', 'Tarjeta de crédito'],
    type: 'cybersource',
    availableIn: [Country.BOLIVIA],
    priority: 2,
  },
]

const CHILE_CONFIG: CountryConfiguration = {
  code: Country.CHILE,
  name: 'Chile',
  capitalizedName: 'Chile',
  paymentMethods: SHARED_PAYMENT_METHODS.filter((method) => method.availableIn.includes(Country.CHILE)),
  paymentTypes: [],
}

const BOLIVIA_CONFIG: CountryConfiguration = {
  code: Country.BOLIVIA,
  name: 'Bolivia',
  capitalizedName: 'Bolivia',
  paymentMethods: SHARED_PAYMENT_METHODS.filter((method) => method.availableIn.includes(Country.BOLIVIA)),
  paymentTypes: [
    {
      label: 'Pago en línea',
      value: PaymentTypes.REDENLACE,
      checked: true,
    },
    {
      label: 'Pago QR/Contra entrega',
      value: PaymentTypes.CONTRA_ENTREGA,
    },
  ],
}

const COUNTRY_CONFIGS: Record<Country, CountryConfiguration> = {
  [Country.CHILE]: CHILE_CONFIG,
  [Country.BOLIVIA]: BOLIVIA_CONFIG,
}

export const getCountryConfig = (country: Country): CountryConfiguration => {
  const config = COUNTRY_CONFIGS[country]
  if (!config) throw new Error(`Country configuration not found: ${country}`)
  return config
}

export const getPaymentMethodsForCountry = (country: Country): PaymentMethodConfig[] => {
  return getCountryConfig(country).paymentMethods
}

export const getPaymentTypesForCountry = (country: Country): PaymentTypeConfig[] => {
  return getCountryConfig(country).paymentTypes
}

export const buildPaymentMethodsEndpoint = (country: Country): string => {
  return `/getPaymentMethods${getCountryConfig(country).capitalizedName}`
}

export const isPaymentMethodAvailable = (methodName: string, country: Country): boolean => {
  return getPaymentMethodsForCountry(country).some((method) => method.regex.test(methodName))
}

export const findPaymentConfigForCountry = (
  name: string,
  methodName: string,
  country: Country,
): PaymentMethodConfig | undefined => {
  return getPaymentMethodsForCountry(country).find((config) => config.regex.test(name) || config.regex.test(methodName))
}
